/**
 * Upload Data — the legacy's intake tab, at parity (ADR-0021).
 *
 * The legacy's ten named slots are the product's real data contract: they tell
 * an operator what to bring, in the firm's own words (`TB`, `PH SALE`,
 * `RR GST`, `RCM REG`). Four abstract "lanes" did the same job with less
 * teaching, so the slots win.
 *
 * Three things carried over exactly because they are load-bearing:
 *
 * - **Files accumulate across picks.** A second pick adds to the first rather
 *   than replacing it, and each attached file has its own ✕. Anyone who has
 *   lost a selection to a stray click knows why.
 * - **The direction guard runs before any bytes move** — a sales-looking file
 *   dropped under Purchase asks first. A warning, never a refusal.
 * - **Per-file progress**, not one bar for the batch. Which file failed is the
 *   only question worth answering when a batch of forty half-works.
 *
 * One thing deliberately NOT carried over: the legacy's job queue and its
 * `/upload/status/<job_id>` polling, with "N uploads ahead of you". Our
 * uploads are per-file POSTs, so the browser already knows each outcome
 * first-hand. Polling would be the same information, arriving later.
 *
 * Two slots (`gstr2b`, `gstr2a`) and two more (`filed_gstr1`, `portal_gstr1`)
 * belong to lanes that do not exist yet — 2B wants portal-Excel parsing, the
 * filed return wants its importer. They accept and store rather than pretend,
 * and say so on the card, because a slot that silently swallows a file is
 * worse than one that admits where it stops.
 */

import { Alert, Button, Card } from '@fiveox/ui'
import { FolderUp, Upload } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DOCUMENT, useEvaluation } from '../lib/access'
import {
  ApiError,
  type DocumentRecord,
  documents,
  intake,
} from '../lib/api'
import { useClient } from './ClientLayout'
import { MappingDialog } from './MappingDialog'

const STAGES = ['intake', 'split', 'parse', 'mapping_review', 'enrichment', 'acceptance'] as const

const STAGE_LABELS: Record<string, string> = {
  intake: 'Intake',
  split: 'Split',
  parse: 'Parsing',
  mapping_review: 'Mapping review',
  enrichment: 'Enrichment',
  acceptance: 'Acceptance',
}

const HELD_LABELS: Record<string, string> = {
  quarantine_unsupported_format: 'Unsupported format',
  quarantine_convert: 'Needs conversion',
  quarantine_password: 'Password-protected',
  quarantine_extraction_unsupported: 'Engine could not read',
  quarantine_extraction_failed: 'Extraction failed',
  review_extraction: 'Needs review',
  wrong_month: 'Wrong month',
  footing_contradiction: 'Disagrees with itself',
  // Parsed, confident, and produced nothing — the failure that used to
  // arrive dressed as a success (X-07 stage 2).
  extraction_empty: 'Parsed but found no rows',
  // The slot said one direction and the engine read the other (D-32). A
  // purchase register read as a sales report used to book straight into GSTR-1.
  direction_conflict: 'Sale or purchase?',
  rejected: 'Rejected',
  superseded: 'Superseded',
}

/**
 * The holds where a mapping profile is offered (ADR-0026 condition 1). The
 * SERVICE enforces this; the list here only decides whether to show a button,
 * and a document that slipped through would be refused on the call.
 */
const PROFILE_HOLDS = new Set([
  'extraction_empty',
  'quarantine_extraction_unsupported',
  'quarantine_extraction_failed',
])

const DISPOSITION_LABELS: Record<string, string> = {
  move: 'Move to its month',
  include_with_reason: 'Include with reason…',
  accept_with_reason: 'Accept with reason…',
  reparse: 'Let it through',
  reject: 'Reject',
}

//: Dispositions the service refuses without a stated reason. This only saves
//: the round trip — the refusal itself lives in the pipeline, where it cannot
//: be skipped by calling the API directly.
const NEEDS_REASON = new Set(['include_with_reason', 'accept_with_reason'])

const SPREADSHEETS = '.xlsx,.xls,.csv'
const DOCS = '.pdf,.jpg,.jpeg,.png,.tif,.tiff,.bmp,.webp,.docx'

/**
 * The ten slots, with the legacy's labels and accept lists verbatim.
 *
 * `direction` drives the pre-upload guard; only the two invoice slots have one,
 * as in the legacy — everywhere else a filename is not evidence of anything.
 * `pending` names a lane not yet built, shown on the card rather than hidden.
 */
interface Slot {
  key: string
  label: string
  hint: string
  accept: string
  direction?: 'sales' | 'purchase'
  pending?: string
}

const SLOTS: Slot[] = [
  {
    key: 'main_data',
    label: 'Main GST Data File',
    hint: 'TB, Sales Inv Ledger, PH SALE, RR GST, RCM REG (for full return)',
    accept: SPREADSHEETS,
  },
  {
    key: 'portal_gstr1',
    label: 'Portal GSTR-1 (SectionWiseSummary)',
    hint: 'From GST Portal — the SectionWiseSummary workbook, or the per-table CSVs',
    accept: SPREADSHEETS,
    pending: 'Stored; the portal-summary comparison is not built yet',
  },
  {
    key: 'filed_gstr1',
    label: 'Filed GSTR-1 (Portal)',
    hint: 'Filed return JSON or SectionWiseSummary — reconciled vs what this app prepared',
    accept: '.json,.xlsx,.xls,.csv',
    pending: 'Stored; prepared-vs-filed reconciliation is not built yet',
  },
  {
    key: 'sales_report',
    label: 'SalesReport (Retail POS)',
    hint: 'Store POS SalesReport Excel/CSV — all stores',
    accept: SPREADSHEETS,
  },
  {
    key: 'sales_invoices',
    label: 'Sales Invoices / B2B',
    // Spreadsheets are refused here in the legacy too: a register dropped into
    // the invoice slot parses as one enormous invoice.
    hint: 'Individual B2B invoices — PDF, image, Word',
    accept: DOCS,
    direction: 'sales',
  },
  {
    key: 'purchase_invoices',
    label: 'Purchase Invoices',
    hint: 'Purchase register, GRN — any format',
    accept: `${SPREADSHEETS},${DOCS}`,
    direction: 'purchase',
  },
  {
    key: 'gstr2b',
    label: 'GSTR-2B (Auto-drafted ITC)',
    hint: 'Portal Excel — caps claimable ITC',
    accept: SPREADSHEETS,
    pending: 'Stored; import 2B as JSON on the Reconciliation tab for now',
  },
  {
    key: 'gstr2a',
    label: 'GSTR-2A (Dynamic)',
    hint: 'Portal Excel — supplier-filed invoices',
    accept: SPREADSHEETS,
    pending: 'Stored; the 2A lane is not built yet',
  },
  {
    key: 'credit_debit_notes',
    label: 'Credit / Debit Notes',
    hint: 'Sales returns, credit memos',
    accept: `${SPREADSHEETS},${DOCS}`,
  },
  {
    key: 'other_files',
    label: 'Other Supporting Files',
    hint: 'E-way bills, HSN master, etc.',
    accept: `${SPREADSHEETS},${DOCS}`,
  },
]

type FileState = 'queued' | 'uploading' | 'done' | 'error'

interface Attached {
  id: string
  file: File
  slot: string
  relativePath: string
  state: FileState
  message?: string
}

function bytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function when(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
}

export function Intake() {
  const client = useClient()
  const { can } = useEvaluation()

  const [rows, setRows] = useState<DocumentRecord[]>([])
  const [error, setError] = useState<string | null>(null)

  // The batch being assembled. Accumulates across picks — never replaced.
  const [attached, setAttached] = useState<Attached[]>([])
  // The client is the URL's (ND-C1) — you upload into the client you are in.
  const clientId = client.id
  const [period, setPeriod] = useState('')
  const [suggestedFrom, setSuggestedFrom] = useState<string | null>(null)
  // A folder that names a DIFFERENT client than the one we are in. The old
  // picker would have quietly switched to it; that is worth saying out loud
  // instead, because filing one client's invoices under another is the
  // expensive mistake here.
  const [suggestMismatch, setSuggestMismatch] = useState<string | null>(null)
  const [running, setRunning] = useState(false)
  const [guard, setGuard] = useState<{
    slot: string
    mismatches: Array<{ filename: string; looks_like: string; reason: string }>
    pending: Attached[]
  } | null>(null)

  const [noteFor, setNoteFor] = useState<string | null>(null)
  const [bindingDoc, setBindingDoc] = useState<DocumentRecord | null>(null)
  // Files this upload run landed in a hold a mapping profile can read
  // past. Offered HERE rather than left to be found on the held table
  // below — the moment somebody knows what the columns mean is the
  // moment they uploaded the file.
  const [needsBinding, setNeedsBinding] = useState<DocumentRecord[]>([])
  const [note, setNote] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const inputs = useRef<Record<string, HTMLInputElement | null>>({})
  const folderInput = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    try {
      const documentBody = await documents.list({ client_id: client.id })
      setRows(documentBody.data)
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Intake could not be loaded.')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const row of rows) if (row.stage) counts[row.stage] = (counts[row.stage] ?? 0) + 1
    return counts
  }, [rows])

  const held = useMemo(
    () => rows.filter((row) => row.held && row.held !== 'superseded'),
    [rows],
  )

  // -- attaching ------------------------------------------------------------

  const addFiles = useCallback(
    async (slot: Slot, list: FileList | null) => {
      if (!list || list.length === 0) return
      const picked: Attached[] = Array.from(list).map((file, i) => ({
        id: `${slot.key}-${Date.now()}-${i}-${file.name}`,
        file,
        slot: slot.key,
        relativePath:
          (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name,
        state: 'queued' as const,
      }))

      // Prefill from the first path we see, once — a suggestion to confirm.
      if (attached.length === 0 && picked[0]) {
        try {
          const body = await intake.suggest(picked[0].relativePath)
          const suggested = body.data.client_name
          // The client cannot be changed from a folder name any more — it is
          // the one in the URL. What a folder name can still do is disagree,
          // and that is worth surfacing.
          const agrees =
            !suggested ||
            client.legal_name.toLowerCase().includes(suggested.toLowerCase()) ||
            suggested.toLowerCase().includes(client.legal_name.toLowerCase())
          setSuggestMismatch(agrees ? null : suggested)
          if (body.data.period) setPeriod(body.data.period)
          setSuggestedFrom(suggested)
        } catch {
          // No suggestion is fine — the fields just stay blank.
        }
      }

      // The direction guard, before anything is committed to the batch.
      if (slot.direction) {
        try {
          const body = await intake.precheck(
            picked.map((p) => p.file.name),
            slot.direction,
          )
          if (body.data.mismatches.length > 0) {
            setGuard({ slot: slot.label, mismatches: body.data.mismatches, pending: picked })
            return
          }
        } catch {
          // The guard is a courtesy; its failure must not block an upload.
        }
      }

      setAttached((cur) => [...cur, ...picked])
    },
    [attached.length, client.legal_name],
  )

  const detach = useCallback((id: string) => {
    setAttached((cur) => cur.filter((a) => a.id !== id))
  }, [])

  // -- uploading ------------------------------------------------------------

  const run = useCallback(async () => {
    if (!clientId || !period || attached.length === 0) return
    setRunning(true)
    setError(null)
    setNeedsBinding([])
    const unreadable: DocumentRecord[] = []
    for (const item of attached) {
      setAttached((cur) => cur.map((a) => (a.id === item.id ? { ...a, state: 'uploading' } : a)))
      try {
        const uploaded = await intake.upload({
          file: item.file,
          client_id: clientId,
          period,
          relative_path: item.relativePath,
          lane: item.slot,
        })
        if (PROFILE_HOLDS.has(uploaded.data.held ?? '')) unreadable.push(uploaded.data)
        setAttached((cur) => cur.map((a) => (a.id === item.id ? { ...a, state: 'done' } : a)))
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Upload failed.'
        setAttached((cur) =>
          cur.map((a) => (a.id === item.id ? { ...a, state: 'error', message } : a)),
        )
      }
    }
    setRunning(false)
    setNeedsBinding(unreadable)
    await load()
  }, [attached, clientId, period, load])

  const dispose = useCallback(
    async (row: DocumentRecord, disposition: string, reason?: string) => {
      setError(null)
      try {
        await documents.dispose(row.id, { disposition, note: reason })
        setNoteFor(null)
        setNote('')
        await load()
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'The disposition failed.')
      }
    },
    [load],
  )

  const removeDocs = useCallback(
    async (ids: string[]) => {
      setError(null)
      const failures: string[] = []
      for (const id of ids) {
        try {
          await documents.remove(id)
        } catch (err) {
          failures.push(err instanceof ApiError ? err.message : 'Delete failed.')
        }
      }
      setSelected(new Set())
      await load()
      // The refusal names the alternative; surface it rather than swallow it.
      if (failures.length > 0) setError(failures[0]!)
    },
    [load],
  )

  const doneCount = attached.filter((a) => a.state === 'done').length
  const errorCount = attached.filter((a) => a.state === 'error').length
  const overall = attached.length === 0 ? 0 : Math.round(((doneCount + errorCount) / attached.length) * 100)

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="gst-h1">
            <Upload size={20} /> Upload Data
          </h1>
          <p className="gst-sub">Every file is in exactly one stage, or held with a reason</p>
        </div>
        {can(DOCUMENT, 'can_create') && (
          <Button type="button" onClick={() => folderInput.current?.click()} variant="outline" size="sm">
            <FolderUp size={16} /> Upload folder
          </Button>
        )}
        <input
          ref={folderInput}
          type="file"
          hidden
          {...({ webkitdirectory: '' } as Record<string, string>)}
          onChange={(e) => void addFiles(SLOTS[0]!, e.target.files)}
        />
      </header>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}

      {/* -- the direction guard (a warning, never a refusal) ---------------- */}
      {guard && (
        <Alert tone="warning" className="mb-6">
          <p className="gst-panel-h">
            Check the slot
            <span className="gst-tag">{guard.slot}</span>
          </p>
          <ul className="gst-panel-list mb-3">
            {guard.mismatches.map((m) => (
              <li key={m.filename}>
                <span className="gst-k">{m.looks_like}</span>
                <span>
                  {m.filename} — {m.reason}
                </span>
              </li>
            ))}
          </ul>
          <p className="mb-3 text-sm">
            These look like they face the other way. Proceed if you know better — nothing has been
            uploaded yet.
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setAttached((cur) => [...cur, ...guard.pending])
                setGuard(null)
              }}
            >
              Proceed
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setGuard(null)}>
              Drop these files
            </Button>
          </div>
        </Alert>
      )}

      {/* -- the ten slots -------------------------------------------------- */}
      {can(DOCUMENT, 'can_create') && (
        <div className="gst-slots mb-6">
          {SLOTS.map((slot) => {
            const mine = attached.filter((a) => a.slot === slot.key)
            return (
              <div
                key={slot.key}
                className={`gst-slot ${mine.length > 0 ? 'has-files' : ''}`}
                onClick={() => inputs.current[slot.key]?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') inputs.current[slot.key]?.click()
                }}
              >
                <p className="gst-slot-t">{slot.label}</p>
                <p className="gst-slot-h">{slot.hint}</p>
                {slot.pending && <p className="gst-slot-p">{slot.pending}</p>}
                <input
                  ref={(el) => {
                    inputs.current[slot.key] = el
                  }}
                  type="file"
                  multiple
                  hidden
                  accept={slot.accept}
                  onChange={(e) => {
                    void addFiles(slot, e.target.files)
                    e.target.value = ''
                  }}
                />
                {mine.length > 0 && (
                  <div className="gst-chips" onClick={(e) => e.stopPropagation()}>
                    {mine.map((a) => (
                      <span key={a.id} className={`gst-filechip is-${a.state}`}>
                        {a.file.name}
                        <span className="gst-filechip-sz">{bytes(a.file.size)}</span>
                        {a.state === 'queued' && (
                          <button
                            type="button"
                            aria-label={`Remove ${a.file.name}`}
                            onClick={() => detach(a.id)}
                          >
                            ✕
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* -- confirm and run ------------------------------------------------ */}
      {attached.length > 0 && (
        <Card className="mb-6 p-4">
          <p className="gst-panel-h text-muted-foreground">
            {attached.length === 1 ? '1 file ready' : `${attached.length} files ready`}
            {running && <span className="gst-tag">{overall}%</span>}
          </p>
          <p className="mb-3 text-xs text-muted-foreground">
            {suggestedFrom
              ? `Folder names suggest the period “${suggestedFrom}” — confirm it before anything books.`
              : 'Confirm the period — nothing is assigned from folder names without you.'}
          </p>

          {suggestMismatch && (
            <Alert tone="warning" className="mb-3">
              <p className="gst-panel-h">These files may belong to another client</p>
              <p className="text-sm">
                The folder names say <strong>{suggestMismatch}</strong>, and you are in{' '}
                <strong>{client.legal_name}</strong>. Nothing has been assigned. If that is
                wrong, switch client before uploading — booking one client&rsquo;s invoices
                under another is not something the return will tell you about later.
              </p>
            </Alert>
          )}

          <div className="mb-3 flex flex-wrap items-end gap-3">
            <label className="gst-label">
              Client
              <span className="gst-input is-fixed">{client.legal_name}</span>
            </label>
            <label className="gst-label">
              Period (MM-YYYY)
              <input
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                placeholder="07-2026"
                className="gst-input w-28"
              />
            </label>
            <Button
              type="button"
              disabled={!clientId || !period || running}
              onClick={() => void run()}
              size="sm"
            >
              {running ? `Processing ${doneCount + errorCount}/${attached.length}…` : 'Process & Generate'}
            </Button>
            <Button
              type="button"
              disabled={running}
              onClick={() => setAttached([])}
              variant="outline" size="sm"
            >
              Clear
            </Button>
          </div>

          {/* per-file rows — which file failed is the only useful question */}
          <div className="gst-table-wrap">
            <table className="gst-table">
              <thead>
                <tr>
                  <th className="is-text">File</th>
                  <th className="is-text">Slot</th>
                  <th>Size</th>
                  <th className="is-text">Status</th>
                </tr>
              </thead>
              <tbody>
                {attached.map((a, i) => (
                  <tr key={a.id} className={i % 2 === 1 ? 'is-a' : ''}>
                    <td className="is-text is-sec">{a.file.name}</td>
                    <td className="is-text">{SLOTS.find((s) => s.key === a.slot)?.label}</td>
                    <td>{bytes(a.file.size)}</td>
                    <td className="is-text">
                      <span
                        className={`gst-chip ${
                          a.state === 'done' ? 'is-ok' : a.state === 'error' ? 'is-stop' : 'is-hold'
                        }`}
                      >
                        {a.state === 'queued'
                          ? 'Queued'
                          : a.state === 'uploading'
                            ? 'Processing'
                            : a.state === 'done'
                              ? 'Done'
                              : 'Error'}
                      </span>
                      {a.message && <span className="ml-2 text-xs">{a.message}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!running && (doneCount > 0 || errorCount > 0) && (
            <p className="gst-meta mt-2">
              {errorCount === 0
                ? `All ${doneCount} file(s) taken. See the stages below.`
                : `${doneCount} taken, ${errorCount} failed — the failures are listed above with their reason.`}
            </p>
          )}
        </Card>
      )}

      {/* -- the stage strip (FR-15) ----------------------------------------- */}
      <div className="gst-strip mb-6">
        {STAGES.map((stage) => (
          <span key={stage} className="gst-strip-i">
            {STAGE_LABELS[stage]}
            <b>{stageCounts[stage] ?? 0}</b>
          </span>
        ))}
        <span className={`gst-strip-i ${held.length > 0 ? 'is-held' : ''}`}>
          Held
          <b>{held.length}</b>
        </span>
      </div>

      {/*
        * Offered at the moment of upload, not left to be found below. The
        * person who just chose these files is the person who knows what their
        * columns mean, and asking them then costs nothing; asking them a day
        * later costs finding the row again.
        *
        * ADR-0026 condition 1 is unchanged — the engine has already tried and
        * failed on every file listed here. This is where the failure happened,
        * not a shortcut past it.
        */}
      {needsBinding.length > 0 && !bindingDoc && (
        <Alert tone="warning" className="mb-4">
          <p className="mb-2">
            The engine could not read{' '}
            <strong>
              {needsBinding.length === 1
                ? 'one file'
                : `${needsBinding.length} of these files`}
            </strong>
            . If you know what their columns mean, bind them now and the binding
            is reused every month.
          </p>
          <span className="flex flex-wrap gap-2">
            {needsBinding.map((doc) => (
              <Button
                key={doc.id}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setBindingDoc(doc)}
              >
                {doc.source_filename ?? 'this file'}
              </Button>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setNeedsBinding([])}
            >
              Later
            </Button>
          </span>
        </Alert>
      )}

      {/* -- the held table --------------------------------------------------- */}
      <h3 className="gst-sub mb-2">Held — these need you</h3>
      {bindingDoc && (
        <MappingDialog
          documentId={bindingDoc.id}
          clientId={client.id}
          filename={bindingDoc.source_filename ?? 'this file'}
          onClose={() => setBindingDoc(null)}
          onApplied={() => {
            setNeedsBinding((cur) => cur.filter((d) => d.id !== bindingDoc.id))
            setBindingDoc(null)
            void load()
          }}
        />
      )}

      {held.length === 0 ? (
        <div className="gst-empty">Nothing is held. Uploads land here when they need you.</div>
      ) : (
        <div className="gst-table-wrap">
          <table className="gst-table">
            <thead>
              <tr>
                <th className="is-text">File</th>
                <th className="is-text">Held for</th>
                <th className="is-text">Period</th>
                <th className="is-text">Next action</th>
              </tr>
            </thead>
            <tbody>
              {held.map((row, i) => (
                <tr key={row.id} className={i % 2 === 1 ? 'is-a' : ''}>
                  <td className="is-text is-sec">
                    {row.source_filename}
                    {row.relative_path && row.relative_path !== row.source_filename && (
                      <small>{row.relative_path}</small>
                    )}
                  </td>
                  <td className="is-text">
                    <span className="gst-chip is-hold">
                      {HELD_LABELS[row.held ?? ''] ?? row.held}
                    </span>
                  </td>
                  <td className="is-text">
                    <span className="gst-mono">{row.period}</span>
                  </td>
                  <td className="is-text">
                    {row.next_action}
                    {/* Its own guard, NOT nested in the dispositions check: a
                        quarantined document has no dispositions, which is
                        exactly the case a mapping profile exists for
                        (ADR-0026 condition 1). */}
                    {can(DOCUMENT, 'can_update') && PROFILE_HOLDS.has(row.held ?? '') && (
                      <span className="mt-2 flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          variant="outline" size="sm"
                          onClick={() => setBindingDoc(row)}
                        >
                          Bind the columns
                        </Button>
                      </span>
                    )}
                    {can(DOCUMENT, 'can_update') && (row.dispositions ?? []).length > 0 && (
                      <span className="mt-2 flex flex-wrap items-center gap-2">
                        {/* Driven by what the SERVICE says is possible. The
                            screen no longer knows which holds are actionable,
                            so a new hold reason needs no change here. */}
                        {(row.dispositions ?? []).map((d) =>
                          NEEDS_REASON.has(d) && noteFor === `${row.id}:${d}` ? (
                            <span key={d} className="flex items-center gap-2">
                              <input
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Why — this is kept"
                                className="gst-input w-56"
                              />
                              <Button
                                type="button"
                                disabled={!note.trim()}
                                onClick={() => void dispose(row, d, note)}
                                variant="outline" size="sm"
                              >
                                Confirm
                              </Button>
                            </span>
                          ) : NEEDS_REASON.has(d) ? (
                            <Button
                              key={d}
                              type="button"
                              onClick={() => {
                                setNoteFor(`${row.id}:${d}`)
                                setNote('')
                              }}
                              variant="outline" size="sm"
                            >
                              {DISPOSITION_LABELS[d] ?? d}
                            </Button>
                          ) : (
                            <Button
                              key={d}
                              type="button"
                              onClick={() => void dispose(row, d)}
                              variant="outline" size="sm"
                            >
                              {DISPOSITION_LABELS[d] ?? d}
                            </Button>
                          ),
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* -- upload history --------------------------------------------------- */}
      <div className="mt-8 mb-2 flex flex-wrap items-center justify-between gap-3">
        <h3 className="gst-sub">Upload history — everything taken for this org</h3>
        {can(DOCUMENT, 'can_delete') && selected.size > 0 && (
          <Button
            type="button"
            variant="outline" size="sm"
            onClick={() => void removeDocs([...selected])}
          >
            Delete selected ({selected.size})
          </Button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="gst-empty">Nothing uploaded yet.</div>
      ) : (
        <div className="gst-table-wrap">
          <table className="gst-table">
            <thead>
              <tr>
                <th className="is-text">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={selected.size === rows.length && rows.length > 0}
                    onChange={(e) =>
                      setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())
                    }
                  />
                </th>
                <th className="is-text">File</th>
                <th className="is-text">Slot</th>
                <th className="is-text">Period</th>
                <th>Size</th>
                <th className="is-text">Uploaded</th>
                <th className="is-text">Where it is</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id} className={i % 2 === 1 ? 'is-a' : ''}>
                  <td className="is-text">
                    <input
                      type="checkbox"
                      aria-label={`Select ${row.source_filename ?? 'file'}`}
                      checked={selected.has(row.id)}
                      onChange={(e) => {
                        const next = new Set(selected)
                        if (e.target.checked) next.add(row.id)
                        else next.delete(row.id)
                        setSelected(next)
                      }}
                    />
                  </td>
                  <td className="is-text is-sec">{row.source_filename}</td>
                  <td className="is-text">
                    {SLOTS.find((s) => s.key === row.source_lane)?.label ?? row.source_lane ?? '—'}
                  </td>
                  <td className="is-text">
                    <span className="gst-mono">{row.period}</span>
                  </td>
                  <td>{bytes(row.size_bytes)}</td>
                  <td className="is-text">{when(row.at)}</td>
                  <td className="is-text">
                    {row.held ? (
                      <span className="gst-chip is-hold">
                        {HELD_LABELS[row.held] ?? row.held}
                      </span>
                    ) : (
                      <span className="gst-chip is-ok">
                        {STAGE_LABELS[row.stage ?? ''] ?? row.stage ?? 'booked'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
